import { apiClient, getApiUrl } from '../lib/apiClient';
import type { 
  School, 
  Area, 
  Class,
  CreateSchoolRequest,
  UpdateSchoolRequest,
  ListQueryParams,
  Pagination,
  ApiResponse 
} from '../types/api';
import { 
  extractListData, 
  extractSingleData, 
  extractPagination,
  type RailsWrappedListResponse,
  type RailsSingleWrappedResponse 
} from '../utils/railsResponseHandler';

// School CRUD operations
export const getSchools = async (params?: ListQueryParams): Promise<{ schools: School[]; pagination: Pagination }> => {
  const response = await apiClient.get<RailsWrappedListResponse<School>>(getApiUrl('/schools'), { params });
  
  const schools = extractListData<School>(response.data);
  const pagination = extractPagination(response.data) || {
    current_page: 1,
    total_pages: 1,
    total_count: schools.length,
    per_page: 20,
    has_previous: false,
    has_next: false
  };
  
  return { schools, pagination };
};

export const getSchool = async (id: string, view?: string): Promise<School> => {
  const params = view ? { view } : {};
  const response = await apiClient.get<RailsSingleWrappedResponse<School>>(getApiUrl(`/schools/${id}`), { params });
  return extractSingleData<School>(response.data);
};

export const createSchool = async (data: CreateSchoolRequest): Promise<School> => {
  const response = await apiClient.post<RailsSingleWrappedResponse<School>>(getApiUrl('/schools'), data);
  return extractSingleData<School>(response.data);
};

export const updateSchool = async (id: string, data: UpdateSchoolRequest): Promise<School> => {
  const response = await apiClient.put<RailsSingleWrappedResponse<School>>(getApiUrl(`/schools/${id}`), data);
  return extractSingleData<School>(response.data);
};

export const deleteSchool = async (id: string): Promise<void> => {
  await apiClient.delete(getApiUrl(`/schools/${id}`)); // OpenAPI returns 204 No Content
};

// School areas
export const getSchoolAreas = async (schoolId: string, params?: ListQueryParams): Promise<ApiResponse<Area[]>> => {
  const response = await apiClient.get<RailsWrappedListResponse<Area>>(getApiUrl(`/schools/${schoolId}/areas`), { params });
  
  const areas = extractListData<Area>(response.data);
  const pagination = extractPagination(response.data);
  
  return {
    data: areas,
    pagination
  };
};

// School classes
export const getSchoolClasses = async (schoolId: string, params?: ListQueryParams): Promise<ApiResponse<Class[]>> => {
  const response = await apiClient.get<RailsWrappedListResponse<Class>>(getApiUrl(`/schools/${schoolId}/classes`), { params });
  
  const classes = extractListData<Class>(response.data);
  const pagination = extractPagination(response.data);
  
  return {
    data: classes,
    pagination
  };
};

// School students - Already handled in studentService
export { getSchoolStudents } from './studentService';